import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

export interface VerisureQrCodeProps {
  value: string;
  size?: number;
  level?: 'L' | 'M' | 'Q' | 'H';
  fgColor?: string;
  bgColor?: string;
  includeMargin?: boolean;
  className?: string;
  id?: string;
  /** Whether to embed the "I am Verisure" branded center badge directly in the QR code (default true) */
  showCenterBadge?: boolean;
  /** Theme for the center badge ('light' | 'dark' | 'orange') */
  badgeTheme?: 'light' | 'dark' | 'orange';
  /** Optional top/bottom writeup banner wrapper */
  showInscription?: boolean;
  inscriptionSize?: 'xs' | 'sm' | 'md' | 'lg';
  /** Extra label displayed below QR (e.g. Tag ID) */
  caption?: string;
}

/**
 * Returns an inline SVG data URI representing the official "I am Verisure" brand badge
 */
export const getVerisureCenterBadgeSvg = (
  theme: 'light' | 'dark' | 'orange' = 'light'
): string => {
  const bg = theme === 'dark' ? '%23090d16' : theme === 'orange' ? '%23ff5a00' : '%23ffffff';
  const stroke = theme === 'orange' ? '%23ffffff' : '%23ff5a00';
  const textIAm = theme === 'dark' ? '%23f8fafc' : theme === 'orange' ? '%23ffffff' : '%230f172a';
  const textBrand = theme === 'orange' ? '%23ffffff' : '%23ff5a00';
  const underline = theme === 'orange' ? '%23ffffff' : '%23ff5a00';

  return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 44" width="120" height="44"><rect x="2" y="2" width="116" height="40" rx="10" fill="${bg}" stroke="${stroke}" stroke-width="3"/><text x="60" y="27" font-family="-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif" font-weight="900" font-size="13" text-anchor="middle" letter-spacing="-0.3"><tspan fill="${textIAm}">I am </tspan><tspan fill="${textBrand}">Verisure</tspan></text><rect x="66" y="31" width="40" height="2.5" rx="1" fill="${underline}"/></svg>`;
};

/**
 * Unified Verisure QR Code component that ensures every generated QR code
 * prominently carries the "I am Verisure" writeup and branding.
 */
export const VerisureQrCode: React.FC<VerisureQrCodeProps> = ({
  value,
  size = 128,
  level = 'H',
  fgColor = '#000000',
  bgColor = 'transparent',
  includeMargin = false,
  className = '',
  id,
  showCenterBadge = true,
  badgeTheme = 'light',
  showInscription = false,
  inscriptionSize = 'md',
  caption,
}) => {
  // Center badge dimensions proportionally scaled to preserve scanability
  const enableCenterBadge = showCenterBadge && size >= 64;
  const badgeWidth = Math.min(Math.round(size * 0.40), 92);
  const badgeHeight = Math.round(badgeWidth * (44 / 120));

  const imageSettings = enableCenterBadge
    ? {
        src: getVerisureCenterBadgeSvg(badgeTheme),
        width: badgeWidth,
        height: badgeHeight,
        excavate: true,
      }
    : undefined;

  const inscriptionClass =
    inscriptionSize === 'xs'
      ? 'text-[9px]'
      : inscriptionSize === 'sm'
      ? 'text-[11px]'
      : inscriptionSize === 'lg'
      ? 'text-base'
      : 'text-xs sm:text-sm';

  const underlineWidth =
    inscriptionSize === 'xs'
      ? 'w-6 h-0.5'
      : inscriptionSize === 'sm'
      ? 'w-8 h-0.5'
      : inscriptionSize === 'lg'
      ? 'w-14 h-1'
      : 'w-10 h-0.5';

  const qrElement = (
    <QRCodeSVG
      id={id}
      value={value}
      size={size}
      level={level}
      fgColor={fgColor}
      bgColor={bgColor}
      includeMargin={includeMargin}
      imageSettings={imageSettings}
    />
  );

  if (!showInscription && !caption) {
    return <div className={`inline-flex items-center justify-center ${className}`}>{qrElement}</div>;
  }

  return (
    <div className={`inline-flex flex-col items-center justify-center text-center ${className}`}>
      {showInscription && (
        <div className="mb-1.5 select-none">
          <div className={`font-black tracking-tight ${inscriptionClass} leading-none text-slate-900 dark:text-white`}>
            I am{' '}
            <span className="text-[#ff5a00] bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 bg-clip-text text-transparent">
              Verisure
            </span>
          </div>
          <div className={`${underlineWidth} bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 mx-auto mt-1 rounded-full`} />
        </div>
      )}

      {qrElement}

      {caption && (
        <div className="mt-1 font-mono font-bold text-[10px] text-slate-500 dark:text-slate-400">
          {caption}
        </div>
      )}
    </div>
  );
};

export interface VerisureStickerDownloadOptions {
  tagId: string;
  url: string;
  outletName?: string;
  logoUrl?: string;
}

/**
 * Downloads a high-resolution branded PNG sticker carrying the official logo,
 * "I am Verisure" writeup, QR code, and activation ledger details.
 */
export const downloadVerisureStickerPng = async ({
  tagId,
  url,
  outletName = 'Verisure Retail Partner',
  logoUrl = '/official.png',
}: VerisureStickerDownloadOptions): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const width = 600;
      const height = 800;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas 2D context not available');
      }

      // Background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // Border with rounded corners
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 4;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(16, 16, width - 32, height - 32, 28);
        ctx.stroke();
      } else {
        ctx.strokeRect(16, 16, width - 32, height - 32);
      }

      // Top Security Badge
      ctx.fillStyle = '#fef2f2';
      ctx.strokeStyle = '#fecaca';
      ctx.lineWidth = 2;
      const badgeY = 40;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(width / 2 - 130, badgeY, 260, 32, 16);
        ctx.fill();
        ctx.stroke();
      }
      ctx.fillStyle = '#b91c1c';
      ctx.font = 'bold 12px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('CERTIFIED SECURITY HARDWARE TAG', width / 2, badgeY + 21);

      // Logo
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.onload = () => {
        const logoHeight = 65;
        const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
        ctx.drawImage(logoImg, width / 2 - logoWidth / 2, 95, logoWidth, logoHeight);

        // "I am Verisure" Text
        const identityY = 200;
        ctx.font = '900 32px Arial, sans-serif';
        ctx.fillStyle = '#0f172a';
        ctx.textAlign = 'center';
        
        // Measure text for proper coloring
        const textPre = 'I am ';
        const textBrand = 'Verisure';
        const preWidth = ctx.measureText(textPre).width;
        const brandWidth = ctx.measureText(textBrand).width;
        const totalTextWidth = preWidth + brandWidth;
        const startX = width / 2 - totalTextWidth / 2;

        ctx.textAlign = 'left';
        ctx.fillText(textPre, startX, identityY);
        ctx.fillStyle = '#ff5a00';
        ctx.fillText(textBrand, startX + preWidth, identityY);

        // Orange underline under Verisure
        ctx.fillStyle = '#ff5a00';
        ctx.fillRect(startX + preWidth, identityY + 6, brandWidth, 4);

        // QR Box
        const qrBoxSize = 340;
        const qrBoxX = width / 2 - qrBoxSize / 2;
        const qrBoxY = 230;
        ctx.fillStyle = '#f8fafc';
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 2;
        if (ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, 20);
          ctx.fill();
          ctx.stroke();
        } else {
          ctx.fillRect(qrBoxX, qrBoxY, qrBoxSize, qrBoxSize);
          ctx.strokeRect(qrBoxX, qrBoxY, qrBoxSize, qrBoxSize);
        }

        // Draw QR
        const badgeSvgUri = getVerisureCenterBadgeSvg('light');
        const badgeImg = new Image();
        badgeImg.onload = () => {
          // Render QR svg by serializing a temporary SVG element
          const tempDiv = document.createElement('div');
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          document.body.appendChild(tempDiv);

          // Build SVG string directly with embedded center badge
          const qrImg = new Image();
          const badgeWidth = Math.round(300 * 0.4);
          const badgeHeight = Math.round(badgeWidth * (44 / 120));

          // Use QRCodeSVG rendered in memory or image
          const svgCode = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300">
            <rect width="100%" height="100%" fill="white"/>
          </svg>`;

          // Simple QR draw onto canvas using DOM
          const qrSvgElement = document.querySelector(`svg[data-tag-qr="${tagId}"]`) || document.querySelector('#qrcode svg') || document.querySelector('.verisure-qr-svg') || document.querySelector('svg.verisure-qr-code');
          
          if (qrSvgElement) {
            const xml = new XMLSerializer().serializeToString(qrSvgElement);
            const svg64 = btoa(unescape(encodeURIComponent(xml)));
            const b64Start = 'data:image/svg+xml;base64,';
            qrImg.src = b64Start + svg64;
          } else {
            // Fallback: draw placeholder qr frame
            ctx.fillStyle = '#1e293b';
            ctx.font = '14px monospace';
            ctx.textAlign = 'center';
            ctx.fillText(url, width / 2, qrBoxY + qrBoxSize / 2);
          }

          qrImg.onload = () => {
            ctx.drawImage(qrImg, qrBoxX + 20, qrBoxY + 20, 300, 300);
            finishCanvas();
          };

          qrImg.onerror = () => {
            finishCanvas();
          };

          const finishCanvas = () => {
            // Tag ID
            ctx.textAlign = 'center';
            ctx.font = '900 24px monospace';
            ctx.fillStyle = '#0f172a';
            ctx.fillText(tagId, width / 2, 610);

            // Outlet Name
            ctx.font = '600 13px Arial, sans-serif';
            ctx.fillStyle = '#64748b';
            ctx.fillText(outletName, width / 2, 635);

            // Instructions Box
            const instrY = 665;
            ctx.fillStyle = '#fff7ed';
            ctx.strokeStyle = '#fed7aa';
            ctx.lineWidth = 1.5;
            if (ctx.roundRect) {
              ctx.beginPath();
              ctx.roundRect(40, instrY, width - 80, 80, 14);
              ctx.fill();
              ctx.stroke();
            } else {
              ctx.fillRect(40, instrY, width - 80, 80);
            }

            ctx.textAlign = 'left';
            ctx.font = 'bold 12px Arial, sans-serif';
            ctx.fillStyle = '#c2410c';
            ctx.fillText('📲 CUSTOMER ACTIVATION GUIDE:', 60, instrY + 24);

            ctx.font = '11px Arial, sans-serif';
            ctx.fillStyle = '#7c2d12';
            ctx.fillText('1. Peel & stick firmly to your hardware or appliance casing', 60, instrY + 44);
            ctx.fillText('2. Scan QR with your phone camera to register & secure warranty', 60, instrY + 62);

            // Download Trigger
            const link = document.createElement('a');
            link.download = `VERISURE-STICKER-${tagId}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            resolve();
          };

          if (!qrSvgElement) {
            finishCanvas();
          }
        };
        badgeImg.src = badgeSvgUri;
      };

      logoImg.onerror = () => {
        // Continue even if logo fails
        resolve();
      };

      logoImg.src = logoUrl;
    } catch (err) {
      reject(err);
    }
  });
};
